import React from "react";

const flattenTreeData = (
  nodes,
  level = 0,
  parentCategoryId = null,
  counter = { current: 0 }
) => {
  let rows = [];

  if (!Array.isArray(nodes)) {
    return rows;
  }

  nodes.forEach((node) => {
    if (!node) return;

    // Skip deactivated items (status === 'D')
    if (node.status === "D") return;

    const hasChildren =
      node.children && Array.isArray(node.children) && node.children.length > 0;

    const type = level === 0 ? "category" : hasChildren ? "sub-branch" : "leaf";

    const uniqueKey = parentCategoryId
      ? `${parentCategoryId}-${node.id}-${counter.current}`
      : `${node.id}-${counter.current}`;

    counter.current += 1;

    rows.push({
      id: uniqueKey,
      category: node.category,
      description: node.description,
      unit: node.unit,
      point: node.point,
      status: node.status || "A", // Add status field with default 'A'
      count: node.count,
      type,
      level,
      parentCategoryId,
      children: hasChildren ? node.children.map((child) => child.id) : [],
      originalId: node.id,
    });

    if (hasChildren) {
      rows = rows.concat(
        flattenTreeData(node.children, level + 1, uniqueKey, counter)
      );
    }
  });

  return rows;
};

export function ExpandedDataTableTreeView({
  treeMenuData,
  tableColumns,
  searchQuery = "",
}) {
  const filterData = (data, query) => {
    // First filter out deactivated items
    const activeItems = data.filter((item) => item.status !== "D");

    if (!query) return activeItems;

    const lowerQuery = query.toLowerCase();
    return activeItems.filter((item) => {
      return (
        (item.category && item.category.toLowerCase().includes(lowerQuery)) ||
        (item.description &&
          item.description.toLowerCase().includes(lowerQuery)) ||
        (item.unit && item.unit.toLowerCase().includes(lowerQuery)) ||
        (item.point && item.point.toString().includes(lowerQuery))
      );
    });
  };

  const tableData = React.useMemo(() => {
    const flattened = flattenTreeData(treeMenuData);
    return filterData(flattened, searchQuery);
  }, [treeMenuData, searchQuery]);

  const getIndentStyle = (row) => {
    if (row.level === 0) return { paddingLeft: "0.5rem" };
    if (row.level === 1) return { paddingLeft: "2rem" };
    return { paddingLeft: `${2 + (row.level - 1) * 1.5}rem` };
  };

  return (
    <div className="flex flex-col space-y-8">
      <div className="w-full overflow-x-auto">
        <table className="min-w-full border-collapse text-sm sm:text-base">
          <thead>
            <tr className="border-b">
              {tableColumns.map((column) => (
                <th
                  key={column.header}
                  className="px-4 py-3 text-left font-semibold text-gray-700"
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row) => {
              if (row.status === "D") return null;
              const indentStyle = getIndentStyle(row);
              const isParent =
                row.type === "category" || row.type === "sub-branch";

              return (
                <tr key={row.id} className="border-b hover:bg-gray-50">
                  {/* Category Name */}
                  <td className="px-2 py-3 font-medium">
                    <div className="flex items-center" style={indentStyle}>
                      <span className="text-gray-800">
                        {row.category}
                        {isParent && (
                          <span className="ml-2 inline-flex items-center rounded-md border px-1.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                            {row.count}
                          </span>
                        )}
                      </span>
                    </div>
                  </td>

                  {/* Description */}
                  <td className="px-4 py-3 text-gray-600">
                    {row.description || "-"}
                  </td>

                  {/* Unit (hidden for parent materials) */}
                  {!isParent && (
                    <td className="px-4 py-3 text-gray-600">
                      {row.unit || "-"}
                    </td>
                  )}
                  {isParent && <td className="px-4 py-3"></td>}

                  {/* Point */}
                  {!isParent && (
                    <td className="px-4 py-3 text-gray-600">
                      {row.point || "-"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
